<template>
    <div
        ref="questionRef"
        class="pt-20 text-gray opacity-100 transition duration-200"
        :class="[state, { 'cursor-pointer': clickable }]"
    >
        <div class="grid grid-cols-12 gap-x-5">
            <div class="tracking-wide text-lg md:text-xl leading-6 text-black col-span-2">
                <span class="text-orange text-2xl font-heading uppercase" v-text="index + 1"></span>/<span
                    v-text="total"
                ></span>
            </div>
            <div class="col-span-10">
                <div class="text-black mb-4 leading-7 md:leading-6 text-lg md:text-xl" @click="setActive">
                    <span class="font-heading uppercase text-xl" v-text="question"></span>
                </div>
                <div :class="{ hidden: !isActive }">
                    <p v-if="explanation" class="font-bold leading-6 mb-4">
                        <span v-html="explanation"></span>
                    </p>
                    <div class="relative mb-4">
                        <div v-if="isNormalInput">
                            <input
                                ref="input"
                                v-model="newValue"
                                v-maska="type === 'date' ? '##-##-####' : ''"
                                :type="type === 'date' ? 'text' : type"
                                :name="name"
                                :placeholder="placeholder"
                                class="shadow border border-gray-400 bg-white py-3 px-5 h-12 w-full font-semibold md:text-lg placeholder:font-semibold placeholder:text-lg placeholder:text-gray-400 text-black focus:border-orange pr-12"
                                v-bind="attributes"
                                :required="required"
                                :pattern="pattern"
                                @keydown.enter.prevent="confirm"
                                @keydown.tab.prevent="confirm"
                            />
                            <div
                                class="bg-teal flex items-center justify-center text-white w-10 aspect-square absolute right-1 top-1 font-heading text-lg uppercase opacity-80 hover:opacity-100 hover:cursor-pointer"
                                @click.stop="confirm"
                                v-text="snippets.form_snippets_application_form_ok_button"
                            ></div>
                        </div>

                        <div v-if="type === 'ratio'">
                            <div ref="input" class="relative mb-4">
                                <button
                                    v-for="(option, optionIndex) in options"
                                    :key="optionIndex"
                                    class="rounded-lg py-3 px-5 text-lg font-semibold border-none shadow mr-3 mb-3 transition duration-200 bg-white hover:cursor-pointer hover:bg-orange focus:outline-none active:outline-none hover:text-white focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
                                    type="button"
                                    :class="{
                                        'relative py-6 pr-20 pl-3 after:content-[\F058] after:font-extrabold after:text-xl after:text-teal after:absolute after:right-3 after:top-6':
                                            newValue === option,
                                    }"
                                    @click.stop="handleSelectClick(option)"
                                    v-text="option"
                                ></button>
                            </div>

                            <input type="hidden" :name="name" :value="newValue" />
                        </div>

                        <div v-if="type === 'select'">
                            <div ref="input" class="relative mb-4">
                                <button
                                    v-for="(option, optionIndex) in options"
                                    :key="optionIndex"
                                    class="rounded-lg py-3 px-5 text-lg font-semibold border-none shadow mr-3 mb-3 transition duration-200 bg-white hover:cursor-pointer hover:bg-orange focus:outline-none active:outline-none hover:text-white focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
                                    type="button"
                                    :class="{
                                        'relative py-3 pr-20 pl-3 after:content-[\F058] after:font-extrabold after:text-xl after:text-teal after:absolute after:right-3 after:top-6':
                                            newValue === option,
                                    }"
                                    @click.stop="handleSelectClick(option)"
                                    v-text="option"
                                ></button>
                            </div>

                            <input type="hidden" :name="name" :value="newValue" />
                        </div>

                        <div v-if="type === 'multiselect'">
                            <div ref="input" class="relative mb-4">
                                <div v-for="(option, optionIndex) in options" :key="optionIndex">
                                    <div
                                        class="rounded-lg py-3 px-5 text-lg font-semibold border-none shadow mr-3 mb-3 transition duration-200 bg-white hover:cursor-pointer hover:bg-orange focus:outline-none active:outline-none hover:text-white focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600 flex flex-row justify-between items-center"
                                        @click="toggleMultiSelectItem(option)"
                                    >
                                        {{ option }}
                                        <i
                                            v-if="multiSelectOptions.includes(option)"
                                            class="fas fa-check-circle text-teal right text-xl"
                                        ></i>
                                    </div>
                                    <div
                                        v-if="
                                            option === 'Duo Topper' ||
                                            (option === 'Duo Topper in Frankrijk' &&
                                                multiSelectOptions.includes(option))
                                        "
                                        class="flex flex-col md:flex-row gap-1 md:gap-4 mr-3 items-start md:items-center whitespace-nowrap"
                                    >
                                        <p class="font-semibold text-black shrink-0">
                                            Vul in met wie je wilt samenwerken:
                                        </p>
                                        <input
                                            v-model="duoTopperInputText"
                                            type="text"
                                            class="shadow border border-gray-400 bg-white py-3 px-5 h-12 grow font-semibold md:text-lg placeholder:font-semibold placeholder:text-lg placeholder:text-gray-400 text-black focus:border-orange pr-12"
                                        />
                                    </div>
                                </div>
                            </div>

                            <button
                                type="button"
                                class="rounded-lg py-6 px-10 text-lg font-semibold border-none shadow mr-3 mb-6 transition duration-200 hover:cursor-pointer hover:bg-orange focus:outline-none active:outline-none right border border-blue bg-blue text-white focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
                                @click.stop="handleSelectClick(multiSelectOptions.toString())"
                            >
                                OK
                            </button>

                            <input type="hidden" :name="name" :value="newValue" />
                        </div>

                        <div v-if="type === 'dateRange'" class="flex flex-col gap-4">
                            <input v-model="newValue" type="hidden" :name="name" />

                            <div class="relative flex flex-row gap-2 items-center">
                                <p class="w-10 text-black font-semibold">Van:</p>
                                <input
                                    ref="input"
                                    v-model="dateRangeFrom"
                                    v-maska="'##-##-####'"
                                    type="text"
                                    :name="`${name}_from`"
                                    :placeholder="placeholder"
                                    class="shadow border border-gray-400 bg-white py-3 pl-5 pr-6 h-12 w-full font-semibold md:text-lg placeholder:font-semibold placeholder:text-lg placeholder:text-gray-400 text-black focus:border-orange pr-12"
                                    v-bind="attributes"
                                    :required="required"
                                    :pattern="pattern"
                                    @change="handleDateRangeChange"
                                    @keydown.enter.prevent="$refs['input2'].focus()"
                                    @keydown.tab.prevent="$refs['input2'].focus()"
                                />
                                <div
                                    class="bg-teal flex items-center justify-center text-white w-10 aspect-square absolute right-1 top-1 font-heading text-lg uppercase opacity-80 hover:opacity-100 hover:cursor-pointer"
                                    @click.stop="$refs['input2'].focus()"
                                    v-text="snippets.form_snippets_application_form_ok_button"
                                ></div>
                            </div>

                            <div class="relative flex flex-row gap-2 items-center">
                                <p class="w-10 text-black font-semibold">tot:</p>
                                <input
                                    ref="input2"
                                    v-model="dateRangeTill"
                                    v-maska="'##-##-####'"
                                    type="text"
                                    :name="`${name}_till`"
                                    :placeholder="placeholder"
                                    class="shadow border border-gray-400 bg-white py-3 pl-5 pr-6 h-12 w-full font-semibold md:text-lg placeholder:font-semibold placeholder:text-lg placeholder:text-gray-400 text-black focus:border-orange pr-12"
                                    v-bind="attributes"
                                    :required="required"
                                    :pattern="pattern"
                                    @change="handleDateRangeChange"
                                    @keydown.enter.prevent="confirm"
                                    @keydown.tab.prevent="confirm"
                                />
                                <div
                                    class="bg-teal flex items-center justify-center text-white w-10 aspect-square absolute right-1 top-1 font-heading text-lg uppercase opacity-80 hover:opacity-100 hover:cursor-pointer"
                                    @click.stop="confirm"
                                    v-text="snippets.form_snippets_application_form_ok_button"
                                ></div>
                            </div>
                        </div>

                        <div v-if="type === 'selectDate'">
                            <div ref="input" class="relative mb-4 zj-question--datepickr">
                                <flatPickr
                                    :key="configDate.defaultDate"
                                    v-model="dateData"
                                    :config="configDate"
                                ></flatPickr>
                                <div
                                    class="hidden zj-question--datepickr flatpickr-input flatpickr-monthDropdown-months flatpickr-current-month arrowUp arrowDown selected"
                                ></div>
                            </div>

                            <input type="hidden" :name="name" :value="newValue" />
                        </div>

                        <div v-if="type === 'selectNextDate'">
                            <div ref="input" class="relative mb-4 zj-question--datepickr">
                                <flat-pickr
                                    v-model="dateDataNext"
                                    :config="configDate"
                                    @on-change="handleNextDateChange"
                                ></flat-pickr>
                            </div>

                            <input type="hidden" :name="name" :value="newValue" />
                        </div>

                        <div v-if="type === 'textarea'">
                            <textarea
                                v-bind="attributes"
                                ref="input"
                                v-model="newValue"
                                :name="name"
                                class="shadow border border-gray-400 bg-white py-3 px-5 h-64 w-full font-semibold md:text-lg placeholder:font-semibold placeholder:text-lg placeholder:text-gray-400 text-black focus:border-orange"
                                @keydown.enter.shift.exact.prevent="confirm"
                                @keydown.tab.prevent="confirm"
                            ></textarea>
                            <button
                                class="absolute right-4 top-5 z-20 bg-blue text-white rounded py-3 px-5 md:text-lg transition duration-200 border-none hover:opacity-100 hover:cursor-pointer flex flex-col h-[calc(100% - 16px)] focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
                                type="button"
                                @click.stop="confirm"
                            >
                                <span class="mt-auto" v-text="snippets.form_snippets_application_form_ok_button"></span>
                            </button>
                        </div>

                        <div v-if="type === 'file'">
                            <label
                                v-show="!isFile"
                                class="block w-full border-dashed border-2 border-gray text-center py-6 px-4"
                                @drop.prevent="setFile"
                            >
                                <span v-text="snippets.form_snippets_application_form_drag_file"></span>
                                <span>&nbsp;</span>
                                <span
                                    class="link--blue"
                                    v-text="snippets.form_snippets_application_form_choose_file"
                                ></span>
                                <input
                                    v-if="fileInputVisible"
                                    v-bind="attributes"
                                    ref="input"
                                    type="file"
                                    :name="name"
                                    class="absolute w-0.5 h-0.5"
                                    @change="setFile($event)"
                                />
                            </label>
                            <div
                                v-if="isFile"
                                class="text-lg text-black relative mb-4 pr-9 after:content-['\F058'] after:font-extrabold after:text-xl after:text-teal after:absolute after:right-3 after:top-1/2 after:transform after:translate-y-2/4"
                                v-text="newValue.name"
                            ></div>
                            <div class="flex flex-row gap-2">
                                <button
                                    class="px-5 py-2 flex items-center border-b border-teal bg-teal text-white flex-grow-0 space-x-3 font-bold hover:bg-teal-600 hover:border-teal-600 transition duration-300 ease-in-out mt-5 focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
                                    type="button"
                                    @click.stop="confirm"
                                    v-text="snippets.form_snippets_application_form_confirm_file"
                                ></button>
                                <button
                                    class="px-5 py-2 flex items-center border-b border-teal bg-teal text-white flex-grow-0 space-x-3 font-bold hover:bg-teal-600 hover:border-teal-600 transition duration-300 ease-in-out mt-5 focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
                                    type="button"
                                    @click.stop="confirm"
                                    v-text="snippets.form_snippets_application_form_file_send_later"
                                ></button>
                            </div>
                            <button
                                v-if="isFile"
                                type="button"
                                class="link link--red"
                                @click.stop="resetNewValue"
                                v-text="snippets.form_snippets_application_form_reset_file"
                            ></button>
                        </div>
                    </div>

                    <div v-if="!isValid" class="text-xs text-red" v-text="validationMessage"></div>

                    <div v-if="isFrance">
                        <p
                            v-if="isNormalInput"
                            class="text-xs text-black"
                            v-text="snippets.form_snippets_application_form_traverse_form"
                        ></p>
                    </div>
                    <div v-else>
                        <p
                            v-if="isNormalInput"
                            class="text-xs text-black"
                            v-text="snippets.form_snippets_application_form_traverse_form"
                        ></p>
                        <p
                            v-if="type === 'textarea'"
                            class="text-xs text-black"
                            v-text="snippets.form_snippets_application_form_traverse_form_textarea"
                        ></p>
                    </div>
                </div>

                <div
                    :class="{ hidden: isActive }"
                    class="text-black text-lg font-semibold pr-12 break-words"
                    @click="setActive"
                >
                    <span v-if="type !== 'file'" v-text="display"></span>

                    <span v-if="isFile" v-text="newValue.name"></span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { French } from "flatpickr/dist/l10n/fr";
import { ref, computed, watch, nextTick, onMounted, onUnmounted, getCurrentInstance, inject } from "vue";

const snippets = inject("statamic_snippets");

const events = ["dragenter", "dragover", "dragleave", "drop"];

const { emit } = getCurrentInstance();

const emits = defineEmits([
    "input",
    "datesdata",
    "update:valid",
    "valid",
    "active-changed",
    "confirmed",
    "active-changed",
]);

const props = defineProps({
    index: {
        type: Number,
        default: 0,
    },
    question: {
        type: String,
        default: "",
    },
    pattern: {
        type: String,
        required: false,
        default: null,
    },
    explanation: {
        type: String,
        default: "",
    },
    placeholder: {
        type: String,
        default: "",
    },
    name: {
        type: String,
        default: "",
    },
    value: {
        type: [String, Number, File],
        default: "",
    },
    total: {
        type: Number,
        default: 1,
    },
    type: {
        type: String,
        default: "text",
    },
    options: {
        type: Array,
        default: () => [],
    },
    defaultDate: {
        type: String,
        default: "",
    },
    attributes: {
        type: Object,
        default: () => {},
    },
    valid: {
        type: Boolean,
        default: false,
    },
    isFrance: {
        type: Boolean,
        default: false,
    },
    required: {
        type: Boolean,
        default: false,
    },
    clickable: {
        type: Boolean,
        default: false,
    },
    active: {
        type: Boolean,
        default: false,
    },
});

const newValue = ref(props.value);
const duoTopperInputText = ref("");
const dateRangeFrom = ref("");
const dateRangeTill = ref("");
const isActive = ref(props.active);
const fileInputVisible = ref(true);
const isValid = ref(false);
const validationMessage = ref("");
const dateData = ref(null);
const dateDataNext = ref(null);
const multiSelectOptions = ref([]);
const configDate = ref({
    dateFormat: "d-m-Y",
    inline: true,
    enable: props.options,
    defaultDate: props.defaultDate,
    locale: French,
});

const input = ref(null);
const input2 = ref(null);
const questionRef = ref(null);

const state = computed(() => {
    if (!isActive.value && isValid.value) {
        return "block relative after:font-extrabold after:text-xl after:text-teal after:absolute after:right-0 after:-mt-2 py-20";
    }

    if (!isActive.value) {
        return "block py-20";
    }

    return "opacity-100 min-h-[16rem]";
});

const isFile = computed(() => newValue.value instanceof File);

const isNormalInput = computed(() => {
    const items = ["text", "email", "tel", "date"];

    return items.includes(props.type);
});

const display = computed(() => newValue.value);

const isValidDate = (date) => {
    const maxDate = new Date(props.attributes.max);
    const minDate = new Date(props.attributes.min);

    // eslint-disable-next-line no-restricted-globals
    return date instanceof Date && !isNaN(date) && !(date > maxDate || date < minDate);
};

const correctDateFormat = (string) => {
    const dateStringArray = string.split("-");
    return new Date(`${dateStringArray[2]}-${dateStringArray[1]}-${dateStringArray[0]}`);
};

const checkDateValid = () => {
    validationMessage.value = input.value.validationMessage;
    const date = correctDateFormat(newValue.value);

    if (isValidDate(date)) {
        newValue.value = date.toLocaleString("nl-NL", { year: "numeric", month: "2-digit", day: "2-digit" });
        isValid.value = true;
        return true;
    }

    isValid.value = false;
    newValue.value = "";
    return false;
};

const handleDateRangeChange = () => {
    const dateFrom = correctDateFormat(dateRangeFrom.value);
    const dateTill = correctDateFormat(dateRangeTill.value);

    if (!isValidDate(dateFrom)) dateRangeFrom.value = "";
    if (!isValidDate(dateTill)) dateRangeTill.value = "";

    const fromDate = dateRangeFrom.value ? dateFrom.toLocaleDateString() : "";
    const tillDate = dateRangeTill.value ? dateTill.toLocaleDateString() : "";

    if (fromDate && tillDate) {
        newValue.value = `van ${fromDate} tot ${tillDate}`;

        return;
    }

    newValue.value = "";
};

const checkValidity = () => {
    if (!isNormalInput.value) {
        if (newValue.value !== "") {
            isValid.value = true;
            return;
        }
        return;
    }

    isValid.value = input.value.checkValidity();
    validationMessage.value = input.value.validationMessage;
};

const confirm = (event) => {
    if (props.type === "date") {
        if (!checkDateValid()) return;
    }

    if (props.type === "dateRange") {
        handleDateRangeChange();
        checkValidity();
    }

    if (!isValid.value && props.required) {
        return;
    }

    isActive.value = false;
    emit("active-changed", props.index + 1);
    emit("confirmed");
};

watch(
    () => props.options,
    (value) => {
        configDate.value.enable = value;
        configDate.value.defaultDate = props.defaultDate;
    }
);

watch(
    () => props.defaultDate,
    (value) => {
        configDate.value.defaultDate = value;
    }
);

watch(newValue, (value) => {
    emit("input", value);

    if (props.type === "file" && !value) {
        // Reset file
        // ToDo: Reset this..
        // this.$refs.input.value = null;
    }

    checkValidity();

    if (props.type !== "file") {
        localStorage[`application_form.${props.name}`] = value;
    }
    configDate.value.defaultDate = value;
});

watch(
    () => props.value,
    (value) => {
        newValue.value = value;

        nextTick(() => {
            checkValidity();

            if (isValid.value && props.type === "multiselect") {
                multiSelectOptions.value = value.split(/,|:/).filter((option) => {
                    if (props.options.includes(option)) {
                        return true;
                    }
                    duoTopperInputText.value = option;
                    return false;
                });
            }
        });
    }
);

watch(
    () => props.active,
    (value) => {
        isActive.value = value;
    }
);

watch(isValid, () => {
    emit("update:valid", isValid.value);
});

const preventDefaults = (e) => {
    e.preventDefault();
};

onMounted(() => {
    if (newValue.value !== "") {
        nextTick(() => {
            checkValidity();

            if (props.type === "date") checkDateValid();

            if (isValid.value) {
                isActive.value = false;
            }
        });
    }

    events.forEach((event) => {
        document.addEventListener(event, preventDefaults);
    });
});

onUnmounted(() => {
    events.forEach((eventName) => {
        document.body.removeEventListener(eventName, preventDefaults);
    });
});

const toggleMultiSelectItem = (option) => {
    const index = multiSelectOptions.value.indexOf(option);

    if (index > -1) {
        multiSelectOptions.value.splice(index, 1);
    } else {
        multiSelectOptions.value.push(option);
    }
};

const returnDoubleDigitNumber = (number) => `0${number}`.slice(-2);

const handleSelectClick = (value) => {
    if (props.type === "multiselect") {
        const multiSelectValues = value.split(",");

        if (!multiSelectValues) {
            return;
        }

        if (multiSelectValues.includes("Duo Topper in Frankrijk")) {
            if (!duoTopperInputText.value) {
                return;
            }
        }

        if (multiSelectValues.includes("Duo Topper") && !duoTopperInputText.value) {
            return;
        }

        if (multiSelectValues.includes("Duo Topper") || multiSelectValues.includes("Duo Topper in Frankrijk")) {
            if (multiSelectValues.includes("Duo Topper")) {
                multiSelectValues[multiSelectValues.indexOf("Duo Topper")] = `Duo Topper:${duoTopperInputText.value}`;
            }
            if (multiSelectValues.includes("Duo Topper in Frankrijk")) {
                multiSelectValues[
                    multiSelectValues.indexOf("Duo Topper in Frankrijk")
                ] = `Duo Topper in Frankrijk:${duoTopperInputText.value}`;
            }

            newValue.value = multiSelectValues.join();

            checkValidity();

            confirm();
            return;
        }
    }
    newValue.value = value;
    checkValidity();

    confirm();
};

const NextDate = (date) => {
    const formatDate = date.split("");
    const newDate = `${formatDate[3]}${formatDate[4]}-${formatDate[0]}${formatDate[1]}-${new Date().getFullYear()}`;
    const currentDate = new Date(newDate);
    const lastDate = new Date(`07-01-${new Date().getFullYear()}`);
    const dates = [];

    for (let d = new Date(newDate); d <= new Date(lastDate.setMonth(10)); d.setDate(d.getDate() + 1)) {
        if (new Date(d).getDay() === 6 && d.getTime() !== currentDate.getTime()) {
            let day = `${new Date(d).getDate()}`;

            if (new Date(d).getDay() + 1 >= 10) {
                day = `0${new Date(d).getDate()}`;
            }

            if (new Date(d).getMonth() + 1 >= 10) {
                dates.push(`${day}-${new Date(d).getMonth() + 1}-${new Date(d).getFullYear()}`);
            } else {
                dates.push(`${day}-0${new Date(d).getMonth() + 1}-${new Date(d).getFullYear()}`);
            }
        }
    }

    return dates;
};

const handleNextDateChange = (selectedDates, dateStr, instance) => {
    const formattedDate = `${returnDoubleDigitNumber(selectedDates[0].getDate())}-${returnDoubleDigitNumber(
        selectedDates[0].getMonth() + 1
    )}-${selectedDates[0].getFullYear()}`;

    handleSelectClick(formattedDate);
    localStorage["application_form.required_date_until_dates"] = JSON.stringify(NextDate(formattedDate));
    emit("datesdata", NextDate(formattedDate));
};

const emitActiveChangedEvent = (number = 0) => {
    emit("active-changed", props.index + number);
};

const setFile = (event) => {
    if (event.type === "drop") {
        newValue.value = event.dataTransfer.files[0];
        return;
    }
    newValue.value = event.target.files[0];
};

const resetNewValue = () => {
    newValue.value = "";
    fileInputVisible.value = false;
    setTimeout(() => {
        nextTick(() => {
            fileInputVisible.value = true;
        });
    }, 10);
};

const focus = () => {
    emitActiveChangedEvent();

    if (props.type === "select") {
        setTimeout(() => {
            nextTick(() => {
                questionRef.value.scrollIntoView({ behavior: "smooth", block: "center" });
            });
        }, 250);
        return;
    }

    nextTick(() => {
        setTimeout(() => {
            questionRef.value.scrollIntoView({ behavior: "smooth", block: "center" });
            input.value.focus({ preventScroll: true });
        }, 250);
    });
};

const setActive = () => {
    if (!props.clickable || isActive.value) {
        return;
    }

    isActive.value = true;
    focus();
};

defineExpose({ input, questionRef, focus });
</script>
